'use client'

import { useEffect } from 'react'

interface KeyboardShortcutsConfig {
  onToggleSidebar: () => void
  onOpenSettings: () => void
  onSkipToNavigation: () => void
  onToggleAudioPlayback?: () => void
  onVisualizerModeChange?: (mode: 'waveform' | 'spectrum' | 'neural' | 'particles' | 'spatial') => void
  isInitialized?: boolean
  audioContextState?: AudioContextState
}

export function useKeyboardShortcuts({
  onToggleSidebar,
  onOpenSettings,
  onSkipToNavigation,
  onToggleAudioPlayback,
  onVisualizerModeChange,
  isInitialized = false,
  audioContextState
}: KeyboardShortcutsConfig) {
  useEffect(() => {
    const handleGlobalKeyPress = (e: KeyboardEvent) => {
      // Sidebar toggle (Ctrl/Cmd + B)
      if (e.key === 'b' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        onToggleSidebar()
      }

      // Settings panel toggle (Ctrl/Cmd + ,)
      if (e.key === ',' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        onOpenSettings()
      }

      // Skip to sidebar navigation (Ctrl/Cmd + S)
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        onSkipToNavigation()
      }

      // Audio playback toggle (Spacebar)
      if (e.key === ' ' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        e.preventDefault()
        if (onToggleAudioPlayback && isInitialized && audioContextState) {
          if (audioContextState === 'running') {
            // Would suspend audio context
            onToggleAudioPlayback()
          } else if (audioContextState === 'suspended') {
            // Would resume audio context
            onToggleAudioPlayback()
          }
        }
      }

      // Visualizer mode shortcuts (1-5)
      if (onVisualizerModeChange && e.key >= '1' && e.key <= '5') {
        e.preventDefault()
        const targetIndex = parseInt(e.key) - 1
        const modes: ('waveform' | 'spectrum' | 'neural' | 'particles' | 'spatial')[] = [
          'waveform', 'spectrum', 'neural', 'particles', 'spatial'
        ]
        if (targetIndex >= 0 && targetIndex < modes.length) {
          onVisualizerModeChange(modes[targetIndex])
        }
      }
    }

    window.addEventListener('keydown', handleGlobalKeyPress)

    return () => window.removeEventListener('keydown', handleGlobalKeyPress)
  }, [
    onToggleSidebar,
    onOpenSettings,
    onSkipToNavigation,
    onToggleAudioPlayback,
    onVisualizerModeChange,
    isInitialized,
    audioContextState
  ])
}